import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Grid,
  Box,
  ButtonGroup,
  Button,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Menu,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import ViewListIcon from "@material-ui/icons/ViewList";
import DateRangeIcon from "@material-ui/icons/DateRange";
import TodayIcon from "@material-ui/icons/Today";
import AssignmentIcon from "@material-ui/icons/Assignment";
import SettingsIcon from "@material-ui/icons/Settings";
import Header from "../header";
import HrLeaveView from "./components/HrLeaveView";
import PublicHolidaysDialog from "./components/PublicHolidaysDialog";
import HrLeaveAccessDialog from "./components/HrLeaveAccessDialog";
import PermissionService from "../../services/PermissionService";
import { getUsers, getUserById } from "../../store/hub/users";
import { getLoggedInUserId } from "../../store/hub/loggedInUser";
import { updateHrLeaveViewModeInStore } from "../../components/common/StoreCommon";

export default function HrLeave() {
  const classes = useStyles();
  const permissionService = new PermissionService();
  const isHrAdmin = permissionService.hrAdminFl();
  const users = useSelector(getUsers)();
  const getUser = useSelector(getUserById);
  const loggedInUserId = useSelector(getLoggedInUserId)();

  const [viewMode, setViewMode] = useState("calendar");
  const [detailsUserId, setDetailsUserId] = useState(loggedInUserId);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState(null);
  const [publicHolidaysOpen, setPublicHolidaysOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);

  useEffect(() => {
    if (loggedInUserId && !detailsUserId) {
      setDetailsUserId(loggedInUserId);
    }
  }, [loggedInUserId, detailsUserId]);

  useEffect(() => {
    updateHrLeaveViewModeInStore(viewMode);
  }, [viewMode]);

  const sortedUsers = useMemo(() => {
    return [...(users || [])].sort((a, b) => {
      const nameA = `${a.FirstName || ""} ${a.LastName || ""}`.trim();
      const nameB = `${b.FirstName || ""} ${b.LastName || ""}`.trim();
      return nameA.localeCompare(nameB);
    });
  }, [users]);

  const showUserSelect = viewMode === "year" || viewMode === "month";

  const headerAdditional = (
    <Box className={classes.headerAdditional}>
      <ButtonGroup size="small" className={classes.viewToggle} aria-label="View mode">
        <Button
          startIcon={<EventAvailableIcon fontSize="small" />}
          className={
            viewMode === "calendar"
              ? `${classes.toggleButton} ${classes.toggleButtonSelected}`
              : classes.toggleButton
          }
          onClick={() => setViewMode("calendar")}
        >
          Calendar
        </Button>
        <Button
          startIcon={<ViewListIcon fontSize="small" />}
          className={
            viewMode === "list"
              ? `${classes.toggleButton} ${classes.toggleButtonSelected}`
              : classes.toggleButton
          }
          onClick={() => setViewMode("list")}
        >
          List
        </Button>
        <Button
          startIcon={<DateRangeIcon fontSize="small" />}
          className={
            viewMode === "year"
              ? `${classes.toggleButton} ${classes.toggleButtonSelected}`
              : classes.toggleButton
          }
          onClick={() => setViewMode("year")}
        >
          Year
        </Button>
        <Button
          startIcon={<TodayIcon fontSize="small" />}
          className={
            viewMode === "month"
              ? `${classes.toggleButton} ${classes.toggleButtonSelected}`
              : classes.toggleButton
          }
          onClick={() => setViewMode("month")}
        >
          Month
        </Button>
        <Button
          startIcon={<AssignmentIcon fontSize="small" />}
          className={
            viewMode === "requests"
              ? `${classes.toggleButton} ${classes.toggleButtonSelected}`
              : classes.toggleButton
          }
          onClick={() => setViewMode("requests")}
        >
          Requests
        </Button>
      </ButtonGroup>

      {showUserSelect && isHrAdmin ? (
        <FormControl variant="outlined" size="small" className={classes.userSelect}>
          <Select
            value={detailsUserId || ""}
            onChange={(e) => setDetailsUserId(e.target.value)}
            displayEmpty
            inputProps={{ "aria-label": "Select user" }}
          >
            {sortedUsers.map((user) => (
              <MenuItem key={user.Id} value={user.Id}>
                {`${user.FirstName} ${user.LastName}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}
    </Box>
  );

  const beforeSos = isHrAdmin ? (
    <>
      <Tooltip title="Settings">
        <IconButton
          size="small"
          onClick={(e) => setSettingsMenuAnchor(e.currentTarget)}
          aria-label="HR Leave settings"
          className={classes.settingsButton}
        >
          <SettingsIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={settingsMenuAnchor}
        open={Boolean(settingsMenuAnchor)}
        onClose={() => setSettingsMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            setSettingsMenuAnchor(null);
            setPublicHolidaysOpen(true);
          }}
        >
          Public Holidays
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSettingsMenuAnchor(null);
            setAccessOpen(true);
          }}
        >
          Access
        </MenuItem>
      </Menu>
    </>
  ) : null;

  const effectiveDetailsUserId = isHrAdmin ? detailsUserId : loggedInUserId;
  const detailsUser = effectiveDetailsUserId
    ? getUser(effectiveDetailsUserId)
    : null;

  return (
    <>
      <Header
        actions={[]}
        additional={headerAdditional}
        beforeSos={beforeSos}
      />
      <Grid container className={classes.root} spacing={0}>
        <Grid item xs={12} style={{ width: "100%", padding: 0 }}>
          <Box className={classes.viewContainer}>
            <HrLeaveView
              viewMode={viewMode}
              detailsUser={detailsUser}
            />
          </Box>
        </Grid>
      </Grid>
      {isHrAdmin ? (
        <>
          <PublicHolidaysDialog
            open={publicHolidaysOpen}
            onClose={() => setPublicHolidaysOpen(false)}
          />
          <HrLeaveAccessDialog
            open={accessOpen}
            onClose={() => setAccessOpen(false)}
          />
        </>
      ) : null}
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexGrow: 1,
    padding: 0,
    margin: 0,
    minHeight: "calc(100vh - 64px)",
    width: "100%",
  },
  viewContainer: {
    backgroundColor: "transparent",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
  },
  headerAdditional: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    height: 64,
    minHeight: 64,
    maxHeight: 64,
    boxSizing: "border-box",
  },
  viewToggle: {
    boxShadow: "none",
    width: "auto",
  },
  toggleButton: {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "16px",
    padding: theme.spacing(0.5, 1.25),
    minWidth: 0,
    color: "rgb(135, 131, 131)",
    borderColor: "rgb(185, 181, 181)",
    backgroundColor: "white",
    "& .MuiButton-startIcon": {
      marginRight: 4,
    },
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
  toggleButtonSelected: {
    color: theme.palette.primary.main,
    backgroundColor: "rgba(25, 118, 210, 0.08)",
    borderColor: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.12)",
    },
  },
  userSelect: {
    minWidth: 200,
    backgroundColor: "white",
  },
  settingsButton: {
    color: "rgb(135, 131, 131)",
  },
}));
